/**
 * Rest-day suggestion logic.
 *
 * Pure, dependency-free module: no React, no gRPC/API calls. Takes the same
 * per-activity data the Dashboard already has (date + optional score) and
 * decides whether to gently suggest a rest day, and why.
 *
 * Two independent signals, either one is enough to suggest a rest day:
 *
 * 1. "no-rest": 7 or more *consecutive* local calendar days each with at
 *    least one activity, counted back from the most recent active day.
 *    Unlike the streak calculation in streaks.ts (which now tolerates rest
 *    gaps of up to two days), this signal specifically looks for an
 *    unbroken run with NO gaps at all - that's the thing worth flagging.
 *
 * 2. "score-drop": the user's recent scored performance (last 3 local days)
 *    is meaningfully below their own baseline (4-21 days ago), i.e.
 *    recentAvg < baselineAvg * 0.8. Both windows need at least 3 scored
 *    activities or this signal simply does not fire - too little data to
 *    say anything.
 *
 *    Activities with no score are ignored entirely for this signal, in both
 *    windows and in the minimum-sample counts. `score` is optional on
 *    Activity; treating a missing score as 0 would manufacture a fake
 *    collapse instead of measuring a real one.
 *
 *    Also: scores are not normalised across sport types, so a stretch of
 *    one harder sport can read as a slump even though nothing is actually
 *    wrong. The ratio-against-own-baseline threshold and the minimum
 *    sample size soften that, but they don't eliminate it - a real
 *    limitation worth stating rather than hiding.
 *
 * Day bucketing mirrors streaks.ts: LOCAL calendar days (getFullYear/
 * getMonth/getDate), diffed via Date.UTC on those local components to avoid
 * DST bugs. The two modules don't share code because they intentionally
 * measure different things (strict consecutiveness vs. rest-tolerant
 * streaks) and keeping them independent keeps each easy to reason about on
 * its own.
 */

export type ActivitySample = {
    date: Date;
    /** Undefined means "no score recorded" - must be ignored, never treated as 0. */
    score?: number;
};

export type RestSuggestion = {
    suggest: boolean;
    reasons: Array<'no-rest' | 'score-drop'>;
    consecutiveDays: number;
    recentAvg: number | null;
    baselineAvg: number | null;
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const NO_REST_THRESHOLD_DAYS = 7;
const RECENT_WINDOW_MAX_DAYS_AGO = 3;
const BASELINE_WINDOW_MIN_DAYS_AGO = 4;
const BASELINE_WINDOW_MAX_DAYS_AGO = 21;
const MIN_SAMPLES_PER_WINDOW = 3;
const SCORE_DROP_RATIO = 0.8;

function toLocalDayKey(date: Date): number {
    return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
}

function dayKeyToComparableTimestamp(key: number): number {
    const year = Math.floor(key / 10000);
    const month = Math.floor((key % 10000) / 100) - 1;
    const day = key % 100;
    return Date.UTC(year, month, day);
}

function dayKeyDiff(laterKey: number, earlierKey: number): number {
    return (dayKeyToComparableTimestamp(laterKey) - dayKeyToComparableTimestamp(earlierKey)) / ONE_DAY_MS;
}

/** Longest unbroken (gap === 1) run of active days ending at the most recent active day. */
function consecutiveDaysEndingAtMostRecent(sortedUniqueDayKeys: number[]): number {
    if (sortedUniqueDayKeys.length === 0) return 0;
    let run = 1;
    for (let i = sortedUniqueDayKeys.length - 1; i > 0; i--) {
        const gap = dayKeyDiff(sortedUniqueDayKeys[i], sortedUniqueDayKeys[i - 1]);
        if (gap === 1) {
            run++;
        } else {
            break;
        }
    }
    return run;
}

function average(values: number[]): number | null {
    if (values.length === 0) return null;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
}

export function computeRestSuggestion(activities: ActivitySample[], now: Date = new Date()): RestSuggestion {
    const todayKey = toLocalDayKey(now);

    // --- Signal 1: no-rest ---
    const uniqueDayKeys = Array.from(new Set(activities.map(a => toLocalDayKey(a.date)))).sort((a, b) => a - b);
    // A run that ended before yesterday is over: it must not trigger advice (nor disagree with a current streak of 0).
    const lastKey = uniqueDayKeys.at(-1);
    const runIsOngoing = lastKey !== undefined && dayKeyDiff(todayKey, lastKey) <= 1;
    const consecutiveDays = runIsOngoing ? consecutiveDaysEndingAtMostRecent(uniqueDayKeys) : 0;
    const noRestFires = consecutiveDays >= NO_REST_THRESHOLD_DAYS;

    // --- Signal 2: score-drop ---
    // Ignore anything without a score entirely - never coerce to 0.
    const scored = activities.filter((a): a is ActivitySample & { score: number } => a.score !== undefined);

    const recentScores: number[] = [];
    const baselineScores: number[] = [];
    for (const activity of scored) {
        const daysAgo = dayKeyDiff(todayKey, toLocalDayKey(activity.date));
        if (daysAgo >= 0 && daysAgo <= RECENT_WINDOW_MAX_DAYS_AGO) {
            recentScores.push(activity.score);
        } else if (daysAgo >= BASELINE_WINDOW_MIN_DAYS_AGO && daysAgo <= BASELINE_WINDOW_MAX_DAYS_AGO) {
            baselineScores.push(activity.score);
        }
    }

    const recentAvg = average(recentScores);
    const baselineAvg = average(baselineScores);
    const hasEnoughSamples = recentScores.length >= MIN_SAMPLES_PER_WINDOW && baselineScores.length >= MIN_SAMPLES_PER_WINDOW;
    const scoreDropFires =
        hasEnoughSamples && recentAvg !== null && baselineAvg !== null && recentAvg < baselineAvg * SCORE_DROP_RATIO;

    const reasons: Array<'no-rest' | 'score-drop'> = [];
    if (noRestFires) reasons.push('no-rest');
    if (scoreDropFires) reasons.push('score-drop');

    return {
        suggest: reasons.length > 0,
        reasons,
        consecutiveDays,
        recentAvg,
        baselineAvg,
    };
}
